import { defineStore } from 'pinia'
import axios from 'axios'
import type { IBank } from '@/definitions/bankDefinition'
import type { ICourtage, IFonds } from '@/definitions/fondsDefinitions'
import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'
import type { ChartData, ChartDataset, DefaultDataPoint } from 'chart.js'
import { barChartBase } from '@/components/ChartConfig/BarChartConfig'
import { Calculator } from '@/controller/calculator'
import { lineChartEnvestorBase } from '@/components/ChartConfig/LineChartConfig'

export const useFondsStore = defineStore(
  'fonds',
  () => {
    const banks: Ref<IBank[]> = ref([
      { name: 'comdirect bank', id: 303 },
      { name: 'FNZ Bank AG', id: 185 },
      { name: 'Fondsdepot Bank', id: 172 },
      { name: 'FIL Fondsbank (FFB)', id: 94 },
      { name: 'DAB Bank', id: 114 },
      { name: 'MorgenFund FFM ', id: 12 },
      { name: 'MorgenFund Lux', id: 459 }
      // { name: 'DWS FFM GmbH', id: 12 },
      // { name: 'Augsburger Aktienbank', id: 135 },
      // { name: 'DWS Top Rente', id: 140 },
      // { name: 'DWS Riester Premium / Vermögensparplan Premium', id: 422 },
      // { name: 'Deutsche Asset Management S.A.', id: 459 },
      // { name: 'DWS Basisrente', id: 3092 },
      // { name: 'DWS DB Vita S.A.', id: 3700 }
    ])

    const defaultInvest = ref(100000)
    const fonds: Ref<IFonds[]> = ref([])
    const selectedBank: Ref<IBank> = ref(banks.value[0])
    const selectedFonds: Ref<IFonds | null> = ref(null)
    const notTradable = computed(() => getCourtage() === null && selectedFonds.value)
    const isInitialized = ref(false)
    const maxDiscount: Ref<Number> = ref(0)
    const lineChartConfig: Ref<ChartDataset<'line', DefaultDataPoint<'line'>>> =
      ref(lineChartEnvestorBase)
    const barChartConfig: Ref<ChartData> = ref(barChartBase)

    function getCourtage(): ICourtage | null {
      if (
        selectedFonds.value === null ||
        !selectedFonds.value?.courtages ||
        selectedFonds.value?.courtages.length === 0
      ) {
        return null
      }
      const res = selectedFonds.value?.courtages.filter((c) => {
        return c.bankNumericId == selectedBank.value.id
      })
      return res && res.length > 0 ? res[0] : null
    }

    function calculate() {
      // calculate line chart
      const calculator = new Calculator({
        period: 10,
        useAgio: true,
        service: 1,
        interest: 6,
        investment: defaultInvest.value,
        courtage: getCourtage()
      })

      calculator.calculate()
      // @ts-ignore
      lineChartConfig.value = calculator.lineChartConfig
      maxDiscount.value = calculator.maxDiscount

      // calculate bar chart
      calculator.calculateBarChartConfig(selectedFonds.value?.courtages || [])
      barChartConfig.value = calculator.barChartConfig
    }

    async function searchFonds(searchText: string) {
      const { data } = await axios.get(
        '/wp-json/envestor-ext/cashback/fonds',
        {
          params: { searchText }
        }
      )
      fonds.value = data ?? []
    }

    async function searchByIsin(isin: string) {
      // Use the proxy endpoint with isin parameter for fast lookup
      const { data } = await axios.get(
        '/wp-json/envestor-ext/cashback/fonds',
        {
          params: { isin }
        }
      )
      if (data && data.length > 0) {
        selectedFonds.value = data[0]
      }
      isInitialized.value = true
    }

    function initializeDefaultSearch() {
      if (!isInitialized.value) {
        searchFonds('default')
        isInitialized.value = true
      }
    }

    watch([selectedFonds, selectedBank], () => {
      if (selectedFonds.value && selectedBank.value) {
        calculate()
      }
    })

    return {
      fonds,
      selectedFonds,
      selectedBank,
      banks,
      defaultInvest,
      maxDiscount,
      notTradable,
      lineChartConfig,
      barChartConfig,
      searchFonds,
      searchByIsin,
      initializeDefaultSearch,
      calculateChart: calculate,
      getCourtage
    }
  },
  {
    persist: false,
    debounce: {
      searchFonds: [
        300,
        {
          isImmediate: false
        }
      ]
    }
  }
)
